import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { HandlerWithResult, makeHandlerChainAwaitable, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { Session } from "@insite/client-framework/Services/SessionService";
import { getShouldLoadInventory, getShouldLoadPricing } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateById } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrand from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrand";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import {
    chain,
    PrepareStructuredPageData,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/GenerateStructuredPageData";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = HandlerWithResult<
    {
        product: ProductModel;
        productInfo?: ProductInfo;
        session: Session;
        brandId?: string | null;
        categoryIdWithBrandId?: string | null;
        categoryId?: string | null;
    },
    { structuredPageData: string }
>;

export const PrepareStructuredPageDataExtended: HandlerType = async props => {
    const {
        id,
        productTitle,
        smallImagePath,
        mediumImagePath,
        largeImagePath,
        canonicalUrl,
        manufacturerItem,
        detail,
        brand: productBrand,
        content,
    } = props.parameter.product;
    const state = props.getState();

    if (content === undefined) {
        /**
         * sometimes content may not be loaded because product was initially loaded for product carousel
         * in this case we should not set structured data and wait until product with all data is loaded
         */
        return false;
    }

    const origin = typeof window !== "undefined" ? window.location.origin : "";

    const fullUrl = `${origin}${canonicalUrl}`;

    const weight = detail?.shippingWeight && detail.shippingWeight > 0 ? detail.shippingWeight : 1;

    const data = {
        "@context": "https://schema.org",
        "@type": "Product",
        productID: id,
        name: productTitle,
        image: [smallImagePath, mediumImagePath, largeImagePath],
        mpn: manufacturerItem,
        sku: detail?.sku,
        url: fullUrl,
        weight: {
            "@type": "QuantitativeValue",
            value: weight,
            unitCode: "LBR",
        },
    } as any;

    if (content?.htmlContent) {
        data.description = content.htmlContent.replace(/<[^>]+>/g, "").replace(/[\r\n\t]/g, "");
    }

    const productInfo = props.parameter.productInfo;
    if (
        productInfo &&
        !props.parameter.product.quoteRequired &&
        getShouldLoadPricing(state) &&
        getShouldLoadInventory(state)
    ) {
        await waitFor(() => !!productInfo.pricing && !!productInfo.inventory);

        const { pricing, inventory, unitOfMeasure } = productInfo;

        if (pricing && inventory && pricing.unitNetPrice > 0) {
            const availability = inventory?.inventoryAvailabilityDtos?.find(
                o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
            )?.availability;

            const isOutOfStock = availability?.messageType === 2;

            const availabilitySchema = isOutOfStock
                ? "https://schema.org/OutOfStock"
                : availability?.messageType === 3
                  ? "https://schema.org/LimitedAvailability"
                  : "https://schema.org/InStock";

            const price = pricing.unitNetPrice;
            const priceCurrency = props.parameter.session?.currency?.currencyCode;

            const availabilityStarts = (() => {
                const d = new Date();
                d.setMonth(d.getMonth() + 2);
                return d.toISOString();
            })();

            const shippingValue = Math.min(100, Math.max(50, price * 0.35));

            data.offers = {
                "@type": "Offer",
                availability: availabilitySchema,
                ...(isOutOfStock ? {} : { availabilityStarts }),
                price,
                priceCurrency,
                url: fullUrl,
                shippingDetails: {
                    "@type": "OfferShippingDetails",
                    shippingRate: {
                        "@type": "MonetaryAmount",
                        value: Number(shippingValue.toFixed(2)),
                        currency: priceCurrency,
                    },
                    shippingDestination: {
                        "@type": "DefinedRegion",
                        addressCountry: "US",
                    },
                    // shippingWeight: {
                    //     "@type": "QuantitativeValue",
                    //     value: weight,
                    //     unitCode: "LBR",
                    // },
                },
            };
        }
    }

    if (props.parameter.brandId) {
        let brandState = getBrandStateById(state, props.parameter.brandId);
        let brand = productBrand || brandState.value;
        if (!brand && !brandState.isLoading) {
            const awaitableLoadBrand = makeHandlerChainAwaitable(loadBrand);
            await awaitableLoadBrand({ brandId: props.parameter.brandId })(props.dispatch, props.getState);
            brandState = getBrandStateById(props.getState(), props.parameter.brandId);
            brand = brandState.value;
        }

        if (brand) {
            const fullBrandUrl = `${origin}${brand?.detailPagePath}`;
            data.brand = {
                "@type": "Brand",
                identifier: brand.id,
                name: brand.name,
                url: fullBrandUrl,
            };
        }
    }

    if (props.parameter.categoryIdWithBrandId || props.parameter.categoryId) {
        const category = getCategoryState(
            state,
            props.parameter.categoryIdWithBrandId || props.parameter.categoryId || "",
        ).value;

        if (category) {
            data.category = category.path;
        }
    }

    props.result = { structuredPageData: JSON.stringify(data) };
};

replaceInChain(chain, PrepareStructuredPageData, PrepareStructuredPageDataExtended);
